import React from 'react';

import Icons from 'assets/icons';

interface ElementDetail {
  id?: string | number;
  subArea?: string;
  focusArea?: string;
  options?: string[];
}

interface RenderableElement {
  relativeToLastYear?: 'Increase' | 'Decrease' | 'Maintain' | string;
  stage?: string;
  metric?: string;
  summary?: string;
  details?: ElementDetail[];
}

const relativeToLastYearSymbols: Record<string, React.JSX.Element> = {
  Increase: <Icons.ArrowUp />,
  Decrease: <Icons.ArrowDown />,
  Maintain: <Icons.Maintain />,
};

const relativeToLastYearClasses: Record<string, string> = {
  Increase: 'text-success-700',
  Decrease: 'text-critical-600',
  Maintain: 'text-gray-600',
};

const subFieldsClasses =
  'bg-gray-100 group-aria-selected:bg-navy-100 py-1 px-1.5 rounded';

const renderIcon = (element: RenderableElement): React.JSX.Element => (
  <span
    aria-label={element.relativeToLastYear}
    className={relativeToLastYearClasses[element.relativeToLastYear ?? '']}
  >
    {relativeToLastYearSymbols[element.relativeToLastYear ?? '']}
  </span>
);

const renderSubArea = (detail: ElementDetail): React.JSX.Element | null => {
  if (!detail.subArea) return null;

  return <span className={subFieldsClasses}>{detail.subArea}</span>;
};

const getOptionsText = (options: string[] | undefined): string => {
  if (!options || !Array.isArray(options)) return '';

  return options.join(', ');
};

const renderDetailContent = (detail: ElementDetail): React.JSX.Element | null => {
  const parts: string[] = [];

  if (detail.focusArea) {
    parts.push(detail.focusArea);
  }

  if (detail.options && detail.options.length) {
    parts.push(getOptionsText(detail.options));
  }

  return parts.length ? (
    <span className="break-words">{parts.join(' - ')}</span>
  ) : null;
};

export const renderCriticalElement = (
  element: RenderableElement | null | undefined
): React.JSX.Element | null => {
  if (!element) return null;

  return (
    <div className="flex flex-wrap">
      <div className="flex flex-wrap items-center gap-1 break-words">
        <>
          <span
            className={relativeToLastYearClasses[element.relativeToLastYear ?? '']}
          >
            {element.stage} {element.metric}
          </span>
          {renderIcon(element)}
        </>

        {element.details &&
          element.details.map((item, detailIndex) => (
            <React.Fragment key={item.id || detailIndex}>
              {renderSubArea(item)}
              <div className="flex flex-wrap items-center">
                {renderDetailContent(item)}
              </div>
              {element.details && detailIndex !== element.details.length - 1 && (
                <Icons.PlusInCircle
                  className="h-4 w-4 text-gray-600"
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          ))}

        {element.summary && (
          <span className="font-normal text-gray-600">{element.summary}</span>
        )}
      </div>
    </div>
  );
};
